"use client";

import type { ReactNode } from "react";
import { useLocale, useTranslations } from "use-intl";

import { CatalogFaqSection, type CatalogFaqItem } from "@/components/catalog-faq-section";
import { Link } from "@/components/link";
import type { CatalogCompanyRelationship } from "@/lib/catalog-credits-companies";
import {
  selectCompanyFaqs,
  selectCreditFaqs,
  selectWorkCompanyFaqs,
  type CompanyFaqRole,
  type CreditFaqFamilyKey,
} from "@/lib/catalog-faq";
import { defaultIfUndefined } from "@garimpa/lib/defaultIfUndefined";
import type { Credit } from "@garimpa/lib/musicTypes";

type LinkedEntity = {
  id: string | null;
  name: string;
  to: string | null;
};

function faqReleaseContext(releaseCountry?: string | null, releaseYear?: string | null) {
  if (releaseCountry && releaseYear) return "countryAndYear";
  if (releaseCountry) return "countryOnly";
  if (releaseYear) return "yearOnly";
  return "withoutDetails";
}

function selectedCompanyFaqs(
  companyScope: "release" | "work",
  companies: CatalogCompanyRelationship[] | undefined,
) {
  if (companyScope === "work") return selectWorkCompanyFaqs(companies);
  return selectCompanyFaqs(companies);
}

export function CatalogCreditsCompaniesFaq(props: {
  artist: string;
  companies?: CatalogCompanyRelationship[];
  companyScope?: "release" | "work";
  creditsByRole: Record<string, Credit[]> | null | undefined;
  releaseCountry?: string | null;
  releaseYear?: string | null;
  title: string;
}) {
  const { artist, companies, creditsByRole, releaseCountry, releaseYear, title } = props;
  const companyScope = defaultIfUndefined(props.companyScope, "release");
  const t = useTranslations("release.faq");
  const locale = useLocale();
  const creditFaqs = selectCreditFaqs(creditsByRole);
  const companyFaqs = selectedCompanyFaqs(companyScope, companies);
  const releaseContext = faqReleaseContext(releaseCountry, releaseYear);

  if (creditFaqs.length === 0 && companyFaqs.length === 0) return null;

  const creditItems = creditFaqs.map<CatalogFaqItem>((faq) => {
    const primary = faq.primary.map(creditToEntity);
    const secondary = faq.secondary.map(creditToEntity);
    const variant =
      primary.length > 0 && secondary.length > 0
        ? "primaryAndSecondary"
        : primary.length > 0
          ? "primaryOnly"
          : "secondaryOnly";
    const key = `credits.${faq.key}` as `credits.${CreditFaqFamilyKey}`;

    return {
      id: `credit-${faq.key}`,
      question: t(`${key}.question`, { artist, title }),
      answer: t.rich(`${key}.answer`, {
        artist,
        title,
        variant,
        primary: () => <LinkedEntityList entities={primary} locale={locale} />,
        secondary: () => <LinkedEntityList entities={secondary} locale={locale} />,
      }),
    };
  });

  const companyItems = companyFaqs.map<CatalogFaqItem>((faq) => {
    const entities = faq.companies.map((company) => ({
      id: company.companyId,
      name: company.companyName,
      to: company.companyId ? `/company/${company.companyId}` : null,
    }));

    if (companyScope === "work") {
      return {
        id: `work-company-${faq.key}`,
        question: t("workCompanies.recorded_at.question", { artist, title }),
        answer: t.rich("workCompanies.recorded_at.answer", {
          artist,
          count: entities.length,
          title,
          companies: () => <LinkedEntityList entities={entities} locale={locale} />,
        }),
      };
    }

    const key = `companies.${faq.key}` as `companies.${CompanyFaqRole}`;
    return {
      id: `company-${faq.key}`,
      question: t(`${key}.question`, {
        artist,
        context: releaseContext,
        country: releaseCountry ?? "",
        title,
        year: releaseYear ?? "",
      }),
      answer: t.rich(`${key}.answer`, {
        artist,
        context: releaseContext,
        country: releaseCountry ?? "",
        title,
        year: releaseYear ?? "",
        companies: () => <LinkedEntityList entities={entities} locale={locale} />,
      }),
    };
  });

  return (
    <>
      <CatalogFaqSection items={creditItems} title={t("creditsTitle")} />
      <CatalogFaqSection
        items={companyItems}
        title={t(companyScope === "work" ? "workCompaniesTitle" : "companiesTitle")}
      />
    </>
  );
}

function creditToEntity(credit: Credit): LinkedEntity {
  return {
    id: credit.artistId,
    name: credit.artistName,
    to: `/artist/${credit.artistId}`,
  };
}

function LinkedEntityList({ entities, locale }: { entities: LinkedEntity[]; locale: string }) {
  const parts = new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).formatToParts(
    entities.map((_, index) => String(index)),
  );

  return parts.map((part, index): ReactNode => {
    if (part.type !== "element") return part.value;
    const entity = entities[Number(part.value)];
    if (!entity) return null;
    if (!entity.to) return <span key={`${entity.name}-${index}`}>{entity.name}</span>;

    return (
      <Link
        appearance="text"
        key={`${entity.id ?? entity.name}-${index}`}
        to={entity.to}
        className="font-semibold text-foreground"
      >
        {entity.name}
      </Link>
    );
  });
}
